'use client';

import { POPULAR_TOPICS } from '../../../lib/prompts/popular-topics.js';
import { AiDisclaimerBanner } from '../AiDisclaimerBanner.js';
import { SlackAvailabilityCallout } from '../../SlackAvailabilityCallout.js';
import { DeepSeekActiveBanner } from '../composer/DeepSeekActiveBanner.js';
import { StartComposer } from './StartComposer.js';
import type { StartScreenComposer } from './useStartScreenComposer.js';

/** Minimal shape this screen reads from the host's business-info context. */
interface StartScreenUserContext {
  name?: string;
  companyName?: string;
}

interface ChatStartScreenFullProps {
  userContext: StartScreenUserContext | null | undefined;
  locale: string;
  isLoading: boolean;
  composer: StartScreenComposer;
  meridianLogoUrl: string;
  showSlackCallout: boolean;
  slackCalloutMentionsSearch: boolean;
  /** MCP connections page — surfaced in the composer's "+" menu. */
  mcpConnectionsHref?: string;
  /** Meridian API base — uploads and the model catalog resolve from it. */
  apiBaseUrl?: string;
  /** Inline model switcher in the composer. */
  showModelPicker?: boolean;
}

/**
 * Start screen (web). Digital minimalism: a short greeting, one centered
 * composer, and a quiet row of starting points — no marketing cards, no walls
 * of copy. Everything secondary (Slack, disclaimer) sits calmly at the bottom.
 */
export function ChatStartScreenFull({
  userContext,
  locale,
  isLoading,
  composer,
  meridianLogoUrl,
  showSlackCallout,
  slackCalloutMentionsSearch,
  mcpConnectionsHref,
  apiBaseUrl,
  showModelPicker = true,
}: ChatStartScreenFullProps) {
  const firstName = typeof userContext?.name === 'string' ? userContext.name : undefined;
  const heading = firstName ? `Hello, ${firstName}` : 'Meet Meridian';

  return (
    <div className="flex-1 overflow-y-auto overscroll-contain">
      <div className="mx-auto flex min-h-full w-full max-w-2xl flex-col justify-center px-4 py-10 sm:py-16">
        {/* Greeting — one line, one supporting line. */}
        <div className="mb-6 flex flex-col items-center text-center">
          <img
            src={meridianLogoUrl}
            alt="Meridian"
            className="mb-4 h-12 w-12 rounded-full object-cover"
          />
          <h1 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
            {heading}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Your WTC operations assistant. What are we working on?
          </p>
        </div>

        {/* The one deliberate surface. */}
        <DeepSeekActiveBanner apiBaseUrl={apiBaseUrl} />
        <StartComposer
          composer={composer}
          isLoading={isLoading}
          apiBaseUrl={apiBaseUrl}
          mcpConnectionsHref={mcpConnectionsHref}
          showModelPicker={showModelPicker}
        />

        {/* Quiet starting points — a single wrapping row of chips, not cards. */}
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          {POPULAR_TOPICS.map((topic) => {
            const label = locale === 'es' ? topic.labelEs : topic.label;
            const prompt = locale === 'es' ? topic.optimizedPromptEs : topic.optimizedPrompt;
            return (
              <button
                key={topic.id}
                type="button"
                onClick={() => composer.handleTopicClick(prompt)}
                className="rounded-full border border-border bg-transparent px-3.5 py-1.5 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:bg-muted hover:text-foreground"
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* Secondary context, calm at the bottom. */}
        <div className="mt-10 space-y-4">
          {showSlackCallout && (
            <div className="hidden sm:block">
              <SlackAvailabilityCallout variant="inline" mentionSearch={slackCalloutMentionsSearch} />
            </div>
          )}
          <AiDisclaimerBanner />
        </div>
      </div>
    </div>
  );
}
